import React, { useRef, useEffect, useState, useMemo } from "react";
import selectedIcon from "../style/selected-icon.svg";
import LoadingSpinner from "../components/LoadingSpinner";

interface PersonOption {
  id: number;
  name: string;
  photo_count: number;
  photo_url?: string | null;
}

interface PeopleFilterDropdownProps {
  people: PersonOption[];
  selectedPersonIds: number[];
  onChange: (ids: number[], clickedId: number) => void;
  onClose: () => void;
  isLoading?: boolean;
  loadingItemId?: number | null;
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0]?.substring(0, 2).toUpperCase() || "?";
};

const PeopleFilterDropdown: React.FC<PeopleFilterDropdownProps> = ({
  people,
  selectedPersonIds,
  onChange,
  onClose,
  isLoading = false,
  loadingItemId = null
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;

      if (ref.current?.contains(target)) return;

      if (target instanceof Element && target.closest(".mobile-filters-modal"))
        return;

      onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const filtered = useMemo(() => {
    const searchTerm = query.trim().toLowerCase();
    return searchTerm
      ? people.filter(p => p.name.toLowerCase().includes(searchTerm))
      : people;
  }, [query, people]);

  const toggle = (id: number) => {
    const newIds = selectedPersonIds.includes(id)
      ? selectedPersonIds.filter(x => x !== id)
      : [...selectedPersonIds, id];
    onChange(newIds, id);
  };

  return (
    <div className="people-filter-dropdown" ref={ref}>
      <div className="people-filter-dropdown__search">
        <i className="fa fa-search people-filter-dropdown__search-icon"></i>
        <input
          type="text"
          className="people-filter-dropdown__search-input"
          placeholder="Search by name"
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
        />
      </div>

      <div className="people-filter-dropdown__grid-container">
        {filtered.length === 0 ? (
          <div className="people-filter-dropdown__empty">No people found</div>
        ) : (
          <div className="people-filter-dropdown__grid">
            {filtered.map(person => {
              const isSelected = selectedPersonIds.includes(person.id);
              const isLoadingThis = isLoading && loadingItemId === person.id;
              const initials = getInitials(person.name);

              return (
                <button
                  key={person.id}
                  type="button"
                  className={`people-card ${
                    isSelected ? "people-card--selected" : ""
                  }`}
                  onClick={() => !isLoadingThis && toggle(person.id)}
                  aria-pressed={isSelected}
                  disabled={isLoadingThis}
                  style={{ cursor: isLoadingThis ? "wait" : "pointer" }}
                >
                  <div className="people-card__avatar">
                    {isLoadingThis ? (
                      <LoadingSpinner
                        size="medium"
                        className="people-card__loading"
                      />
                    ) : (
                      <>
                        {person.photo_url ? (
                          <img
                            src={person.photo_url}
                            alt={person.name}
                            className="people-card__image"
                          />
                        ) : (
                          <span className="people-card__initials">
                            {initials}
                          </span>
                        )}
                        {isSelected && (
                          <img
                            src={selectedIcon}
                            alt="Selected"
                            className="people-card__check"
                          />
                        )}
                      </>
                    )}
                  </div>
                  <div className="people-card__name">{person.name}</div>
                  <div className="people-card__count">
                    {person.photo_count} photo
                    {person.photo_count !== 1 ? "s" : ""}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PeopleFilterDropdown;
